/**
 * @module
 *
 * @import {UserProfileData} from '../model/user-profile.js'
 */
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {guard} from '@acng/frontend-rubicon';
import {assign} from '@acng/frontend-bounty/object.js';
import {RemoveController, NamespaceController} from 'nova';

import userDummySrc from '../../../basic/img/noImg.png';
import {asset, media} from '../../core/service/env.js';
import {user} from '../user.js';
import {get, put} from '../../core/service/backend.js';
import {USER_PROFILE_DATA} from '../model/user-profile.js';
import {GUEST} from '../model/dictionary.js';
import {ProfileChange} from './change.js';
import {Preference} from './preference.js';

/**
 * @type {Promise<UserProfileData> | undefined | null}
 */
let response;

/**
 * Change user profile
 */
class UserProfile extends NamespaceController {
  /**
   * @type {UserProfile | undefined}
   */
  static #singleton;
  change = new ProfileChange(this);
  preference = new Preference(this, 'preference');
  experience = new Preference(this, 'experience');
  lookingfor = new Preference(this, 'lookingfor');
  male = new RemoveController(false);
  female = new RemoveController(false);

  /**
   * @type {UserProfileData | undefined | null}
   */
  #data;

  constructor() {
    if (UserProfile.#singleton) {
      return UserProfile.#singleton;
    }

    super();
    UserProfile.#singleton = this;
  }

  /** @override */
  get hidden() {
    if (user[GUEST]) {
      this.#data = undefined;
      response = undefined;

      return true;
    }

    return false;
  }

  /**
   * @returns {Promise<UserProfileData | null>}
   *
   * @override
   */
  get setup() {
    return (response ??= this.#fetchUserProfileData());
  }

  /**
   * @returns {UserProfileData}
   *
   * @throws {UserProfile}
   */
  get data() {
    const ctrl = this;

    if (!ctrl.#data) {
      throw ctrl;
    }

    return ctrl.#data;
  }

  get image() {
    const images = this.#data?.images;

    return images ? media.user + images.wide[480] : asset(userDummySrc);
  }

  /**
   * @param {Partial<UserProfileData>} data
   *
   * @returns {Promise<void>}
   */
  async put(data) {
    await response;
    const putResponse = put('userProfile/update', data);
    const profileData = await putResponse;
    ASSERT: guard(profileData, USER_PROFILE_DATA);

    response ??= putResponse;
    this.#data ??= profileData;
    assign(this.#data, profileData);

    this.male.condition = profileData.gender == 'm';
    this.female.condition = profileData.gender == 'f';
  }

  /**
   * @returns {Promise<UserProfileData>}
   */
  async #fetchUserProfileData() {
    const data = await get('userProfile/view');
    ASSERT: guard(data, USER_PROFILE_DATA);

    this.#data = data;
    this.male.condition = data.gender == 'm';
    this.female.condition = data.gender == 'f';

    return data;
  }
}

export {UserProfile};
